import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@etherisc/ui-kit';
import { farmerService } from '../../services/farmerService';
import { usePermissions } from '../../hooks/usePermissions';
import { ApiError } from '../../utils/errorHandling';
import { FarmerForm } from '../../components/farmer';
import type { Farmer, FarmerFormData } from '../../types/farmer';
import { NeutralButton } from '../../components/buttons';
import { AlertTriangleIcon } from 'lucide-react';

export function EditFarmer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const returnTo = searchParams.get('returnTo');
  
  const [farmer, setFarmer] = useState<Farmer | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const showSuccess = useCallback((title: string, description: string) => {
    return toast.success(title, { description });
  }, []);

  const showError = useCallback((title: string, description: string) => {
    return toast.error(title, { description });
  }, []);

  const loadFarmer = useCallback(async () => {
    if (!id) {
      showError('Invalid Farmer ID', 'No farmer ID provided');
      navigate('/admin/farmers');
      return;
    }

    try {
      setLoading(true);
      const response = await farmerService.getFarmerById(id);
      
      if (response.farmer) {
        setFarmer(response.farmer);
      } else {
        throw new Error('No farmer data received');
      }
    } catch (error) {
      console.error('Error loading farmer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load farmer details';
      showError('Error Loading Farmer', errorMessage);
      navigate('/admin/farmers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showError]);

  useEffect(() => {
    loadFarmer();
  }, [loadFarmer]);

  const handleSubmit = useCallback(async (data: FarmerFormData) => {
    if (!farmer) return;
    
    try {
      setIsSubmitting(true);
      
      // Only send fields that are actually changed and non-empty
      const submitData: Partial<FarmerFormData> = {};
      
      // Helper function to check if a value should be included
      const shouldInclude = (newValue: unknown, originalValue: unknown) => {
        if (newValue === undefined || newValue === null) return false;
        if (typeof newValue === 'string' && newValue.trim() === '') return false;
        if (Array.isArray(newValue) && newValue.length === 0) return false;
        return JSON.stringify(newValue) !== JSON.stringify(originalValue);
      };

      // Basic fields
      if (shouldInclude(data.name?.trim(), farmer.name)) {
        submitData.name = data.name.trim();
      }
      if (shouldInclude(data.phone?.trim(), farmer.phone)) {
        submitData.phone = data.phone.trim();
      }
      if (shouldInclude(data.email?.trim() || undefined, farmer.email)) {
        submitData.email = data.email?.trim() || undefined;
      }

      // Location data
      if (shouldInclude(data.countryCode, farmer.countryCode)) {
        submitData.countryCode = data.countryCode;
      }
      if (shouldInclude(data.administrativeLevels, farmer.administrativeLevels)) {
        submitData.administrativeLevels = data.administrativeLevels;
      }
      // Coordinates: detect clearing (had value -> now empty) as well as changes
      const latCleared = (farmer.latitude !== null && farmer.latitude !== undefined) && 
                         (data.latitude === undefined || data.latitude === null);
      const lngCleared = (farmer.longitude !== null && farmer.longitude !== undefined) && 
                         (data.longitude === undefined || data.longitude === null);
      if (latCleared || lngCleared) {
        // Explicitly send null to clear coordinates
        (submitData as Record<string, unknown>).latitude = null;
        (submitData as Record<string, unknown>).longitude = null;
      } else {
        if (shouldInclude(data.latitude, farmer.latitude)) {
          submitData.latitude = data.latitude;
        }
        if (shouldInclude(data.longitude, farmer.longitude)) {
          submitData.longitude = data.longitude;
        }
      }
      // Farm location - always send both fields together
      if (shouldInclude(data.farmerLocation, farmer.farmerLocation) || 
          shouldInclude(data.hasSeparateFarmerLocation, farmer.hasSeparateFarmerLocation)) {
        submitData.hasSeparateFarmerLocation = data.hasSeparateFarmerLocation;
        submitData.farmerLocation = data.farmerLocation;
      }

      // Agricultural data
      if (shouldInclude(data.primaryActivity?.trim(), farmer.primaryActivity)) {
        submitData.primaryActivity = data.primaryActivity?.trim();
      }
      if (shouldInclude(data.otherActivities, farmer.otherActivities)) {
        submitData.otherActivities = data.otherActivities;
      }
      if (shouldInclude(data.area, farmer.area)) {
        submitData.area = data.area;
      }

      // Payment and documentation
      if (shouldInclude(data.paymentDetails, farmer.paymentDetails)) {
        submitData.paymentDetails = data.paymentDetails;
      }
      if (shouldInclude(data.proofOfStake, farmer.proofOfStake)) {
        submitData.proofOfStake = data.proofOfStake;
      }

      // Organization
      if (shouldInclude(data.organizationIds, farmer.organizationIds)) {
        submitData.organizationIds = data.organizationIds;
      }

      const response = await farmerService.updateFarmer(farmer.id, submitData);
      
      if (response.farmer) {
        showSuccess(
          'Farmer Updated Successfully',
          `${response.farmer.name}'s information has been updated.`
        );
        navigate(returnTo || '/admin/farmers');
      } else {
        throw new Error('No farmer data received from server');
      }
    } catch (error) {
      console.error('Error updating farmer:', error);
      
      if (error instanceof ApiError) {
        showError('Failed to Update Farmer', error.message);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        showError('Failed to Update Farmer', errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [farmer, navigate, showSuccess, showError]);

  const handleCancel = useCallback(() => {
    navigate(returnTo || '/admin/farmers');
  }, [navigate, returnTo]);

  const handleDeleteFarmer = useCallback(async () => {
    if (!farmer) return;

    const confirmed = confirm(
      `Are you sure you want to permanently delete farmer "${farmer.name}"?\n\n` +
      `This action will:\n` +
      `• Remove all farmer data permanently\n` +
      `• Remove associated quotes and policies\n` +
      `• Cannot be undone\n\n` +
      `Type "DELETE" to confirm this permanent action.`
    );

    if (!confirmed) return;

    try {
      await farmerService.deleteFarmer(farmer.id, true);
      showSuccess(
        'Farmer Deleted',
        `${farmer.name} has been permanently removed from the system.`
      );
      navigate('/admin/farmers');
    } catch (error) {
      console.error('Error deleting farmer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete farmer';
      showError('Delete Failed', errorMessage);
    }
  }, [farmer, navigate, showSuccess, showError]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading farmer details...</span>
        </div>
      </div>
    );
  }

  if (!farmer) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Farmer not found or you don't have permission to view it.</p>
          <NeutralButton type="button" onClick={handleCancel}>
            Back to Farmers
          </NeutralButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FarmerForm
        mode="edit"
        initialData={farmer}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        title="Edit Farmer"
        subtitle={`Update information for ${farmer.name}`}
      />

      {/* Danger Zone - Only for admins */}
      {hasPermission('farmers:delete') && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertTriangleIcon className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Danger Zone</h3>
                <p className="text-red-700 mb-4">
                  Permanently delete this farmer and all associated data. This action cannot be undone.
                </p>
                <NeutralButton
                  type="button"
                  onClick={handleDeleteFarmer}
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
                >
                  Delete Farmer Permanently
                </NeutralButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}